/**
 * The search a guest is looking at, read off the catalogue's address bar — what
 * the sidebar's «Guardar tu búsqueda» stashes before it sends him to /register
 * (Spain, `features.guestSearchCarried`, persona test 22.10, D1).
 *
 * The link used to go to /register and nothing else, so a guest who had filtered
 * the catalogue (CPV 45000000 + Andalucía + budget ≥ 100 000) came back from
 * registration with 0 saved searches and was asked «¿Qué ofreces?» from scratch.
 * The filter-bar button (`SaveSearchButton`) always stashed; the sidebar link
 * never did. This reads the same params the catalogue route reads (`q`,
 * `region`, `category`, `ikpu`, `niche`, `minBudget`, `maxBudget`) and builds the
 * same {@link PendingSavedSearch} the button builds, so the replay after sign-in
 * (`PendingSavedSearchSync`) creates it unchanged.
 *
 * Only on the catalogue's own list pages: `/`, `/catalog` and `/page/N`. Anywhere
 * else the query string is not a catalogue filter (a lot, /plans, /results), and
 * the link stays a plain link.
 */
import { stripLocalePrefix } from '../i18n/locale-path';
import type { PendingSavedSearch } from './pending-saved-search';

/** Display names the stash can use for its label — resolved by the caller from
 *  the catalogue's cached region/category lists; any may be missing. */
export interface GuestSearchNames {
  /** Region name by slug (`ES61` → «Andalucía»). */
  region?: (slug: string) => string | undefined;
  /** Category name by slug. */
  category?: (slug: string) => string | undefined;
  /** The label when nothing names the search — «Mi búsqueda». */
  fallback: string;
}

/** A positive whole number, or nothing — what `parseIntParam` accepts, minus zero. */
function positiveInt(value: string | null): number | undefined {
  if (value === null || value.trim() === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : undefined;
}

function isCatalogueListPath(pathname: string): boolean {
  const bare = stripLocalePrefix(pathname);
  return bare === '/' || bare === '' || bare === '/catalog' || bare.startsWith('/page/');
}

/**
 * The search to stash for a guest at `from` (`pathname + search`), or `null` when
 * the page carries none: not a catalogue list, or no subject and no budget.
 */
export function guestSearchFromAddress(
  from: string,
  names: GuestSearchNames,
): PendingSavedSearch | null {
  let url: URL;
  try {
    url = new URL(from, 'https://guest.invalid');
  } catch {
    return null;
  }
  if (!isCatalogueListPath(url.pathname)) return null;
  const params = url.searchParams;
  const pick = (key: string): string | undefined => {
    const value = params.get(key)?.trim();
    return value ? value : undefined;
  };
  const keyword = pick('q');
  const regionCode = pick('region');
  const categorySlug = pick('category');
  const ikpuCode = pick('ikpu');
  const nicheSlug = pick('niche');
  const minBudgetUzs = positiveInt(params.get('minBudget'));
  const maxBudgetUzs = positiveInt(params.get('maxBudget'));
  if (
    !keyword &&
    !regionCode &&
    !categorySlug &&
    !ikpuCode &&
    !nicheSlug &&
    minBudgetUzs === undefined &&
    maxBudgetUzs === undefined
  ) {
    return null;
  }

  // Same shape as `SaveSearchButton.suggestedName()`: subject · region · "words".
  const parts: string[] = [];
  if (nicheSlug) {
    parts.push(
      nicheSlug
        .split(',')
        .filter(Boolean)
        .map((slug) => slug.replace(/-/g, ' '))
        .join(', '),
    );
  } else if (categorySlug) {
    parts.push(
      names.category?.(categorySlug) ??
        categorySlug
          .split(',')
          .filter(Boolean)
          .map((slug) => names.category?.(slug) ?? slug)
          .join(', '),
    );
  }
  // A region code is no label: with the list not cached yet, leave it out of the name.
  const regionName = regionCode ? names.region?.(regionCode) : undefined;
  if (regionName) parts.push(regionName);
  if (keyword) parts.push(`"${keyword}"`);

  return {
    name: parts.length ? parts.join(' · ') : names.fallback,
    keyword,
    regionCode,
    categorySlug,
    ikpuCode,
    nicheSlug,
    ...(minBudgetUzs !== undefined ? { minBudgetUzs } : {}),
    ...(maxBudgetUzs !== undefined ? { maxBudgetUzs } : {}),
  };
}
